import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { api } from '../utils/api'

function IncomeForm() {
  const { getToken } = useAuth()
  const [incomeEntries, setIncomeEntries] = useState([])
  const [formData, setFormData] = useState({
    source: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    is_recurring: false,
    description: ''
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadIncome()
  }, [])

  const loadIncome = async () => {
    try {
      const data = await api.get('/api/income', getToken)
      setIncomeEntries(data)
    } catch (error) {
      console.error('Error loading income:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.source || !formData.amount || !formData.date) {
      alert('Please fill in all required fields')
      return
    }

    setLoading(true)
    try {
      await api.post('/api/income', {
        ...formData,
        amount: parseFloat(formData.amount)
      }, getToken)

      // Reset form
      setFormData({
        source: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        is_recurring: false,
        description: ''
      })
      loadIncome()
    } catch (error) {
      console.error('Error adding income:', error)
      alert('Failed to add income entry')
    }
    setLoading(false)
  }

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this income entry?')) return

    try {
      await api.delete(`/api/income?id=${id}`, getToken)
      loadIncome()
    } catch (error) {
      console.error('Error deleting income:', error)
    }
  }

  const totalIncome = incomeEntries.reduce((sum, entry) => sum + entry.amount, 0)
  const recurringIncome = incomeEntries
    .filter(e => e.is_recurring)
    .reduce((sum, entry) => sum + entry.amount, 0)

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Income Tracking</h2>
        <div className="text-right">
          <div className="text-sm text-gray-600">Total Income</div>
          <div className="text-2xl font-bold text-green-600">${totalIncome.toFixed(2)}</div>
          <div className="text-xs text-gray-500">Recurring: ${recurringIncome.toFixed(2)}/month</div>
        </div>
      </div>

      {/* Add Income Form */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Income Entry</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                placeholder="e.g., Salary, Freelance, Investment"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div className="flex items-center pt-6">
              <input
                type="checkbox"
                id="is_recurring"
                checked={formData.is_recurring}
                onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_recurring" className="ml-2 text-sm text-gray-700">
                Recurring monthly income
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Additional notes"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Adding...' : 'Add Income'}
          </button>
        </form>
      </div>

      {/* Income List */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Income History</h3>
        {incomeEntries.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No income entries yet. Add your first entry above!</p>
        ) : (
          <div className="space-y-2">
            {incomeEntries.map((entry) => (
              <div key={entry.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900">{entry.source}</span>
                    {entry.is_recurring && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Recurring</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {new Date(entry.date).toLocaleDateString()}
                    {entry.description && ` • ${entry.description}`}
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-lg font-bold text-green-600">+${entry.amount.toFixed(2)}</span>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default IncomeForm

